import React, { useState, useCallback,useEffect } from 'react';
import PatientInfo from './PatientInfo';
import { useUltrasoundForm } from '../hooks/useUltrasoundForm';
import DraftRestoreBanner from './DraftRestoreBanner';
import { useTechnologists } from '../hooks/useTechnologists';

const INITIAL_PEDIATRIC_HIPS_DATA = {
  examType: 'Pediatric Hips',
  examDate: new Date().toISOString().split('T')[0],
  firstName: '',
  lastName: '',
  mrn: '',
  technologist: '',
  reason: '',
  callReport: false,
  phoneNumber: '',
  history: {
    breechPresentation: false,
    prematurity: false,
    familyHistoryDDH: false
  },
  comments: '',
  rightHip: {
    evaluated: false,
    status: '',
    instability: false,
    jointEffusion: false,
    alphaAngle: ''
  },
  leftHip: {
    evaluated: false,
    status: '',
    instability: false,
    jointEffusion: false,
    alphaAngle: ''
  }
};

function PediatricHipsForm({ token, onBack, facilityInfo }) {
  // Tab state: 0=Right Hip, 1=Left Hip
  const [activeTab, setActiveTab] = useState(0);
  const [showRightAlphaTooltip, setShowRightAlphaTooltip] = useState(false);
  const [showLeftAlphaTooltip, setShowLeftAlphaTooltip] = useState(false);
  // Fetch dynamic technologists list
  const { technologists, defaultTech } = useTechnologists();

  const { formData, updateField, updateNestedField, handleSubmit: hookHandleSubmit,
    handlePreview, resetForm, isLoading, error, success, clearMessages, fieldErrors, draftBannerProps } = useUltrasoundForm(
    {
      ...INITIAL_PEDIATRIC_HIPS_DATA,
      technologist: defaultTech
    },
      'Pediatric Hips',
    { token, facilityInfo }
  );
  useEffect(() => {
   if (defaultTech && !formData.technologist) {
     updateField('technologist', defaultTech);
   }  
  }, [defaultTech, formData.technologist]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    await hookHandleSubmit(e);
  };

  return (
    <div className="form-container">
      <button onClick={onBack} className="back-btn" type="button">
        ← Back to Worksheet Selection
      </button>

      <form onSubmit={handleFormSubmit}>
        {/* Status Messages */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <strong>Success:</strong> {success}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {/* Draft restore banner */}
        {draftBannerProps && <DraftRestoreBanner {...draftBannerProps} />}

        {/* Patient Information */}
        <PatientInfo formData={formData} updateField={updateField} technologists={technologists} fieldErrors={fieldErrors} />

        {/* History */}
        <div className="form-section">
          <h3>History</h3>
          <div className="form-group" style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <label>
              <input
                type="checkbox"
                checked={formData.history.breechPresentation}
                onChange={(e) => updateNestedField('history', 'breechPresentation', e.target.checked)}
              />
              {' '}Breech Presentation
            </label>

            <label>
              <input
                type="checkbox"
                checked={formData.history.prematurity}
                onChange={(e) => updateNestedField('history', 'prematurity', e.target.checked)}
              />
              {' '}Prematurity
            </label>

            <label>
              <input
                type="checkbox"
                checked={formData.history.familyHistoryDDH}
                onChange={(e) => updateNestedField('history', 'familyHistoryDDH', e.target.checked)}
              />
              {' '}Family History of DDH
            </label>
          </div>
        </div>

        {/* TAB NAVIGATION */}
        <div className="form-section">
          <div className="worksheet-tabs">
            <button
              type="button"
              onClick={() => setActiveTab(0)}
              className={`worksheet-tab-btn${activeTab === 0 ? ' active' : ''}`}
            >
              Right Hip
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(1)}
              className={`worksheet-tab-btn${activeTab === 1 ? ' active' : ''}`}
            >
              Left Hip
            </button>
          </div>

          {/* Tab Content */}
          <div className="worksheet-tab-content" style={{ minHeight: '400px' }}>
            {/* Right Hip Tab */}
            {activeTab === 0 && (
              <div className="tab-content-grid">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.rightHip.evaluated}
                      onChange={(e) => updateNestedField('rightHip', 'evaluated', e.target.checked)}
                    />
                    {' '}Right Hip Evaluated
                  </label>
                </div>

                <div style={{ opacity: formData.rightHip.evaluated ? 1 : 0.4, pointerEvents: formData.rightHip.evaluated ? 'auto' : 'none' }}>
                  <h3 style={{ color: 'var(--color-primary)' }}>Right Hip</h3>
            
            <div className="form-group">
              <label>Status:</label>
              <select
                value={formData.rightHip.status}
                onChange={(e) => updateNestedField('rightHip', 'status', e.target.value)}
                disabled={!formData.rightHip.evaluated}
              >
                <option value="">Select status</option>
                <option value="Normal">Normal</option>
                <option value="Abnormal">Abnormal</option>
              </select>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.rightHip.instability}
                  onChange={(e) => updateNestedField('rightHip', 'instability', e.target.checked)}
                  disabled={!formData.rightHip.evaluated}
                />
                {' '}Instability
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.rightHip.jointEffusion}
                  onChange={(e) => updateNestedField('rightHip', 'jointEffusion', e.target.checked)}
                  disabled={!formData.rightHip.evaluated}
                />
                {' '}Joint Effusion
              </label>
            </div>

            <div className="form-group" style={{ position: 'relative' }}>
              <label>
                Alpha Angle (degrees):
                <span 
                  style={{ 
                    marginLeft: '8px', 
                    cursor: 'help',
                    color: 'var(--color-primary)',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                  onMouseEnter={() => setShowRightAlphaTooltip(true)}
                  onMouseLeave={() => setShowRightAlphaTooltip(false)}
                >
                  ℹ️
                </span>
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.rightHip.alphaAngle}
                onChange={(e) => updateNestedField('rightHip', 'alphaAngle', e.target.value)}
                placeholder="Enter alpha angle"
                disabled={!formData.rightHip.evaluated}
              />
              {showRightAlphaTooltip && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  zIndex: 1000,
                  backgroundColor: 'var(--color-card)',
                  border: '2px solid var(--color-primary)',
                  borderRadius: '8px',
                  padding: '10px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  marginTop: '5px'
                }}>
                  <img 
                    src="/images/hips.png" 
                    alt="Hip alpha angle measurement guide" 
                    style={{ 
                      maxWidth: '1200px', 
                      height: 'auto',
                      display: 'block'
                    }} 
                  />
                </div>
              )}
            </div>
                </div>
              </div>
            )}

            {/* Left Hip Tab */}
            {activeTab === 1 && (
              <div className="tab-content-grid">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.leftHip.evaluated}
                      onChange={(e) => updateNestedField('leftHip', 'evaluated', e.target.checked)}
                    />
                    {' '}Left Hip Evaluated
                  </label>
                </div>

                <div style={{ opacity: formData.leftHip.evaluated ? 1 : 0.4, pointerEvents: formData.leftHip.evaluated ? 'auto' : 'none' }}>
                  <h3 style={{ color: 'var(--color-primary)' }}>Left Hip</h3>
            
            <div className="form-group">
              <label>Status:</label>
              <select
                value={formData.leftHip.status}
                onChange={(e) => updateNestedField('leftHip', 'status', e.target.value)}
                disabled={!formData.leftHip.evaluated}
              >
                <option value="">Select status</option>
                <option value="Normal">Normal</option>
                <option value="Abnormal">Abnormal</option>
              </select>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.leftHip.instability}
                  onChange={(e) => updateNestedField('leftHip', 'instability', e.target.checked)}
                  disabled={!formData.leftHip.evaluated}
                />
                {' '}Instability
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.leftHip.jointEffusion}
                  onChange={(e) => updateNestedField('leftHip', 'jointEffusion', e.target.checked)}
                  disabled={!formData.leftHip.evaluated}
                />
                {' '}Joint Effusion
              </label>
            </div>

            <div className="form-group" style={{ position: 'relative' }}>
              <label>
                Alpha Angle (degrees):
                <span 
                  style={{ 
                    marginLeft: '8px', 
                    cursor: 'help',
                    color: 'var(--color-primary)',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                  onMouseEnter={() => setShowLeftAlphaTooltip(true)}
                  onMouseLeave={() => setShowLeftAlphaTooltip(false)}
                >
                  ℹ️
                </span>
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.leftHip.alphaAngle}
                onChange={(e) => updateNestedField('leftHip', 'alphaAngle', e.target.value)}
                placeholder="Enter alpha angle"
                disabled={!formData.leftHip.evaluated}
              />
              {showLeftAlphaTooltip && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  zIndex: 1000,
                  backgroundColor: 'var(--color-card)',
                  border: '2px solid var(--color-primary)',
                  borderRadius: '8px',
                  padding: '10px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  marginTop: '5px'
                }}>
                  <img 
                    src="/images/hips.png" 
                    alt="Hip alpha angle measurement guide" 
                    style={{ 
                      maxWidth: '1200px', 
                      height: 'auto',
                      display: 'block'
                    }} 
                  />
                </div>
              )}
            </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Comments */}
        <div className="form-section">
          <h3>Comments</h3>
          <textarea
            rows="8"
            value={formData.comments}
            onChange={(e) => updateField('comments', e.target.value)}
            placeholder="Additional comments..."
            style={{ width: '100%', minHeight: '150px', fontSize: '16px' }}
          />
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handlePreview}
            disabled={isLoading}
            className="preview-btn"
          >
            Preview Report
          </button>
          <button 
            type="submit" 
            disabled={isLoading} 
            className="submit-btn"
          >
            {isLoading ? 'Generating Report...' : 'Generate & Save Report'}
          </button>
          <button 
            type="button"
            onClick={resetForm}
            disabled={isLoading}
            className="reset-btn"
          >
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}

export default PediatricHipsForm;
